import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Button,
  Card,
  Radio,
  Form,
  Input,
  Skeleton,
  Row,
  Col,
  Select,
  DatePicker,
} from "antd";
import moment from "moment";

import { GlobalOutlined, PlusOutlined } from "@ant-design/icons";

import {
  AddressControl,
  ContactField,
  fieldValidity,
  Messages,
  Notify,
  openNotification,
  progressIndicatorEnum,
  util
} from "@seims/shared";

import candidateLookup from "../../services/candidateLookupService";
import commonLookup from "../../services/commonLookupService";
import api from "../../services/nysService";
import { getProfile } from "@seims/shared/src/security";
import { recordError } from "../../utils/logService";

const PersonalInformation = ({ onProgressUpdate, data, readOnly }) => {
  const [titles, setTitles] = useState([]);
  const [races, setRaces] = useState([]);
  const [genders, setGenders] = useState([]);
  const [address, setAddress] = useState("default");
  const [showModal, setShowModal] = useState(false);
  const [showAddressInfo, setshowAddressInfo] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [userData, setUserData] = useState(getProfile());
  const [contactValue, setContactValue] = useState("");
  const [altContactValue, setAltContactValue] = useState("");
  const [idValidStatus, setIdValidStatus] = useState(fieldValidity.SUCCESS);
  const [idValidityMsg, setIdValidityMsg] = useState("");
  const [idRequired, setIDRequired] = useState(true);
  const [addressValidity, setAddressValidity] = useState(fieldValidity.SUCCESS);
  const [addressMsg, setAddressMsg] = useState("");
  const [addressRequired, setAddressRequired] = useState(true);
  const [active, setActive] = useState(true);
  const [demographyData, setDemographyData] = useState();
  const [isDisabled, setIsDisabled] = useState(false);
  const [provinces, setProvince] = useState([]);
  const [activities, setActivities] = useState([]);

  const formItemLayout = {
    layout: "vertical",
  };

  const { Option } = Select;
  const { Meta } = Card;
  const [form] = Form.useForm();
  const formName = "PersonalInformationForm";

  const idRef = useRef(null);
  const addressRef = useRef(null);

  useEffect(() => {
    getTitles();
    getRaces();
    getGenders();
    getProvinces();
    getPreNYSActivity();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      disability: false,
      // address: "default",
    });
    getDemographics();
  }, [titles, genders, races, data]);

  const getDemographics = async () => {
    const { firstName, lastName } = userData;
    try {
      setActive(false);

      form.setFieldsValue({
        firstname: firstName,
        surname: lastName,
      });

      if (!util.isEmpty(data)) {
        setActive(true);

        onProgressUpdate(progressIndicatorEnum.COMPLETED);
        const selectedTitle = titles.find(
          (record) => record.key === data.title
        );

        const selectedGender = genders.find(
          (record) => record.key === data.gender
        );

        const selectedActivity = activities.find(
          (record) => record.key === data.preNYSActivity
        );

        const selectedRace = races.find((record) => record.key === data.race);

        const selectedPreferredProvince = provinces.find(
          (record) => record.key === data.preferredProvince
        );

        const selectedHomeProvince = provinces.find(
          (record) => record.key === data.homeProvince
        );

        form.setFieldsValue({
          id: data.id,
          firstname: data.name,
          surname: data.surname,
          title: selectedTitle,
          idNumber: data.idNumber,
          gender: selectedGender,
          genderOther: data.gender_other,
          race: selectedRace,
          preNYSActivity: selectedActivity,
          preferredProvince: selectedPreferredProvince,
          dob: moment(data.dob),
          address: data.address,
          disability: data.disability,
          alternativePersonFullName: data.alternativePersonFullName,
          homeProvince: selectedHomeProvince,
        });

        setDemographyData(data);
        setAltContactValue(data.alternativeCellNumber);
        setContactValue(data.cellNumber);

        setAddress(data.address);
        setshowAddressInfo(true);
        setActive(false);
        setIsDisabled(data.disability);
      }
    } catch (error) {}
  };
  const getPreNYSActivity = async () => {
    try {
      const response = await candidateLookup.getPreNYSActivity();
      if (response) {
        const { data } = response;
        setActivities(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const onNameBlur = (e) => {
    const { value, name } = e.target;
    const textValue = util.toPascalText(value);
    form.setFieldsValue({ [name]: textValue });
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleModalSubmit = (values) => {
    let _address = { ...values, country: values.country.value };

    _address = {
      ...values,
      country: values.country.value,
      province: values.province.value,
    };

    setAddress(_address);
    setShowModal(false);
    setshowAddressInfo(true);

    const validAddress =
      _address.street != undefined &&
      _address.cityOrTown != undefined &&
      _address.province != undefined &&
      _address.postalCode != undefined;

    if (validAddress) {
      setAddressMsg("");
      setAddressRequired(false);
      setAddressValidity(fieldValidity.SUCCESS);
      form.setFieldsValue({
        address: _address,
      });
    }
  };

  const getTitles = async () => {
    try {
      const response = await commonLookup.getTitles();
      if (response) {
        const { data } = response;
        setTitles(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getRaces = async () => {
    try {
      const response = await commonLookup.getRaces();
      if (response) {
        const { data } = response;
        setRaces(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getGenders = async () => {
    try {
      const response = await commonLookup.getGenders();
      if (response) {
        const { data } = response;
        setGenders(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getProvinces = async () => {
    try {
      const response = await commonLookup.getProvinces();
      if (response) {
        const { data } = response;
        setProvince([...data.sort(util.setDynamicSort("value"))]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const handleContactChange = (value) => {
    if (value === 0 || value.length < 10) {
      form.setFieldsValue({ cellNumber: undefined });
      return;
    }
    form.setFieldsValue({ cellNumber: value });
  };

  const handleAltContactChange = (value) => {
    if (value === 0 || value.length < 10) {
      form.setFieldsValue({ alternativeCellNumber: undefined });
      return;
    }
    form.setFieldsValue({ alternativeCellNumber: value });
  };

  const onIdChange = (e) => {
    const { value } = e.target;
    const idNumber = util.toNumeric(value);
    const isValid = util.isSAIDNumberValid(idNumber);

    if (!isValid) {
      setIdValidityMsg("Please enter a valid RSA ID Number");
      setIDRequired(true);
      setIdValidStatus(fieldValidity.ERROR);
    } else {
      setIdValidityMsg("");
      setIDRequired(false);
      setIdValidStatus(fieldValidity.SUCCESS);
      let dob = idNumber.toString().substring(0, 6);
      let year = dob.substring(0, 2);

      const _dob = moment(
        year >= parseInt("00") && year <= parseInt("15")
          ? "20" + dob
          : "19" + dob
      );

      form.setFieldsValue({ dob: _dob });
    }

    form.setFieldsValue({ idNumber: idNumber });

  };

  const checkAge = (e) => {
    let msg = "";
    const { value } = e.target;
    const idNumber = util.toNumeric(value);

    let dob = idNumber.toString().substring(0, 6);
    let year = dob.substring(0, 2);

    const _dob = moment(
      year >= parseInt("00") && year <= parseInt("15") ? "20" + dob : "19" + dob
    );

    let birthdate = new Date(moment(_dob).format("YYYY,M,DD"));
    let today = new Date();
    let year_difference = today.getFullYear() - birthdate.getFullYear();
    let one_or_zero =
      today.getMonth() < birthdate.getMonth() ||
        (today.getMonth() === birthdate.getMonth() &&
          today.getDate() < birthdate.getDate())
        ? 1
        : 0;
    let age = year_difference - one_or_zero;

    if (age > 35) {
      msg = util.parseMessage(
        Messages.AGE_LIMIT_EXCEEDED,
        "Personal info detail(s)"
      );

      openNotification("Age validation", msg, Notify.WARNING);
      idRef.current.focus();
      return;
    }

    //Check agaist the server ID
    isIdNumberUnique(idNumber);
  };

  const isIdNumberUnique = async (idNumber) => {
    try {
      const dbIdNumber = data.idNumber;
      const response = await api.getUniqueIdNumber(idNumber);

      if (!dbIdNumber && response && response.status === 204) {
        form.setFieldsValue({ idNumber: idNumber });
      }
      else if (!dbIdNumber && response && response.status === 200) {
        setIdValidityMsg(`***${idNumber}*** ID Number already exists`);
        setIDRequired(true);
        setIdValidStatus(fieldValidity.ERROR);

        //!If Id number was previously saved in the db then use it otherwise clear
        form.setFieldsValue({ idNumber: dbIdNumber ? dbIdNumber : "" });
      }

      return data;
    } catch (error) {
      recordError(error);
    }
  };

  const clearFormContents = () => {
    form.resetFields();
    setAddress({});
    setShowModal(false);
    setshowAddressInfo(false);
    setShowOther(false);
  };

  const onTitleChange = (selected) => {
    const selectedTitle = titles.find((record) => record.key == selected);
    form.setFieldsValue({ title: selectedTitle });
  };

  const onRaceChange = (selected) => {
    const selectedRace = races.find((record) => record.key == selected);
    form.setFieldsValue({ race: selectedRace });
  };

  const setGenderOther = (selected) => {
    const otherGender = genders.find((record) => record.value === "Other");
    const selectedGender = genders.find((record) => record.key == selected);
    form.setFieldsValue({ gender: selectedGender });
    setShowOther(selected.includes(otherGender?.key.toString()));
  };

  const onChangeCheck = (e) => {
    const { value } = e.target;
    // setIsDisabled(value);
    form.setFieldsValue({ disability: value });
  };

  const onCurrentPlacementStatusChange = (selected) => {
    const selectedCurrEmpStatus = activities.find(
      (record) => record.key == selected
    );
    form.setFieldsValue({ preNYSActivity: selectedCurrEmpStatus });
  };

  const onProvinceChange = (selected) => {
    const selectedProvince = provinces.find((record) => record.key == selected);
    form.setFieldsValue({ preferredProvince: selectedProvince });
  };
  const onHomeProvinceChange = (selected) => {
    const selectedProvince = provinces.find((record) => record.key == selected);
    form.setFieldsValue({ homeProvince: selectedProvince });
  };

  const onSubmit = async (values) => {
    try {
      const user = getProfile();

      const {
        title,
        firstname,
        surname,
        idNumber,
        disability,
        gender,
        genderOther,
        race,
        dob,
        preferredProvince,
        preNYSActivity,
        cellNumber,
        alternativePersonFullName,
        alternativeCellNumber,
        homeProvince,
      } = values;

      const demographics = {
        id: demographyData?.id > 0 ? demographyData.id : "-1",
        title: title,
        name: firstname,
        surname: surname,
        idNumber: idNumber,
        // disability: isDisabled,
        disability: disability,
        gender: gender,
        genderOther: genderOther,
        race: race,
        dob: new Date(dob),
        preferredProvince: preferredProvince,
        preNYSActivity: preNYSActivity,
        email: user.emailAddress,
        userId: user.userId,
        cellNumber: cellNumber,
        alternativePersonFullName: alternativePersonFullName,
        alternativeCellNumber: alternativeCellNumber,
        address: address,
        homeProvince: homeProvince,
      };

      onProgressUpdate(progressIndicatorEnum.IN_PROGRESS);
      const request = await api.addDemographics(demographics);
      var msg;

      if (request.status == 200 || request.status == 201) {
        onProgressUpdate(progressIndicatorEnum.COMPLETED);
        msg = util.parseMessage(
          Messages.ADD_SUCCESS,
          "Personal info detail(s)"
        );
        openNotification("Save Personal Info", msg, Notify.SUCCESS);
        return;
      }

      msg = util.parseMessage(Messages.ADD_FAILURE, "Personal info detail(s)");
      openNotification("Save Personal Info", msg, Notify.ERROR);
    } catch (error) {
      onProgressUpdate(progressIndicatorEnum.NOT_STARTED);
      openNotification(
        "Save Personal Info",
        "SYSTEM FAILED TO SAVE",
        Notify.ERROR
      );
      recordError(error);
    }
  };

  const disabledDates = (current) => {
    // return current && current > moment().endOf("day");
    return current && current.valueOf() > Date.now();
  };

  const onInitiationChange = (selectedDate) => {
    form.setFieldsValue({ dob: selectedDate });
  };

  const onSubmitFailed = (failedValues) => {

    const namePath = failedValues.errorFields[0].name;

    form.getFieldInstance(namePath)?.focus();
    const { values } = failedValues;

    if (values.idNumber === undefined) {
      setIdValidityMsg("A valid RSA ID Number is required");
      setIDRequired(true);
      setIdValidStatus(fieldValidity.ERROR);
    }

    if (values.address === "default" || values.address === undefined) {
      setAddressMsg("A valid address is required");
      setAddressRequired(true);
      setAddressValidity(fieldValidity.ERROR);
    }

  };
  const disabilityLabel = `${isDisabled ? "Yes" : "No"}`;

  const handleKeyDown = (event) => {
    if (event.key === "Backspace") {
      form.setFieldsValue({ dob: "" });
    }
  };

  return (
    <>
      <h1 className="center-align">Personal Information</h1>
      <p className="sub-title center-align">
        Please fill in your personal information
      </p>
      <br />
      {active ? (
        <Skeleton active={active} />
      ) : (
        <>
          <Form
            {...formItemLayout}
            name={formName}
            form={form}
            onFinish={onSubmit}
            onFinishFailed={onSubmitFailed}
            scrollToFirstError
          >
            <Row>
              <Col xs={24} md={10}>
                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true, message: "Please select title" }]}
                >
                  <Select onChange={onTitleChange}>
                    {titles.map((title) => (
                      <Option key={title.key}> {title.value}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col md={2}></Col>
              <Col xs={24} md={10}>
                <Form.Item
                  name="firstname"
                  label="Firstname"
                  rules={[
                    {
                      pattern: new RegExp("[A-Za-z]"),
                      message: "Provided charaters not allowed"
                    },
                    {
                      required: true,
                      message: "Please enter your first name"
                    }
                  ]}
                >
                  <Input name="firstname" onBlur={onNameBlur} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={10}>
                <Form.Item
                  name="surname"
                  label="Lastname"
                  rules={[
                    {
                      pattern: new RegExp("[A-Za-z]"),
                      message: "Provided charaters not allowed"
                    },
                    {
                      required: true,
                      message: "Please enter your surname"
                    }
                  ]}
                >
                  <Input name="surname" onBlur={onNameBlur} />
                </Form.Item>
              </Col>
              <Col span={2}></Col>
              <Col xs={24} md={10}>
                <Form.Item
                  name="idNumber"
                  label="ID Number"
                  onBlur={checkAge}
                  validateStatus={idValidStatus}
                  help={idValidityMsg}
                  rules={[
                    {
                      required: idRequired,
                      message: idValidityMsg,
                    },
                  ]}
                >
                  <Input
                    ref={idRef}
                    name="idNumber"
                    onChange={onIdChange}
                    minLength={13}
                    maxLength={13}
                    onKeyDown={handleKeyDown}
                    //? Disable Id if it exists in DB
                    disabled={data.idNumber}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={10}>
                <Form.Item
                  name="disability"
                  label="Disability"
                  rules={[{ required: true }]}
                >
                  <Radio.Group name="disability" onChange={onChangeCheck}>
                    <Radio key={1} value={true}>
                      Yes
                    </Radio>
                    <Radio key={2} value={false}>
                      No
                    </Radio>
                    {disabilityLabel}
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={2}></Col>
              <Col xs={24} md={10}>
                <Form.Item
                  name="gender"
                  label="Gender"
                  rules={[
                    { required: true, message: "Please select your gender" },
                  ]}
                >
                  <Select onSelect={setGenderOther} onChange={setGenderOther}>
                    {genders.map((gender) => (
                      <Option key={gender.key}>{gender.value}</Option>
                    ))}
                  </Select>
                </Form.Item>
                {showOther && (
                  <Form.Item
                    name="genderOther"
                    label="Please specify preferred gender"
                    rules={[
                      {
                        required: true,
                        message: "Please specify preferred gender",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={10}>
                <Form.Item
                  name="race"
                  label="Race"
                  rules={[
                    { required: true, message: "Please select your race" },
                  ]}
                >
                  <Select onChange={onRaceChange}>
                    {races.map((race) => (
                      <Option key={race.key}>{race.value}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={2}></Col>
              <Col xs={24} md={10}>
                <Form.Item
                  name="dob"
                  label="Date of birth"
                  rules={[
                    {
                      required: true,
                      message: "Please select your date of birth",
                    },
                  ]}
                >
                  <DatePicker
                    onChange={onInitiationChange}
                    disabledDate={disabledDates}
                    // defaultValue={moment()}

                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>

              <Col xs={24} md={10}>
                <Form.Item
                  name="cellNumber"
                  label="Cellphone"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your cellphone number",
                    },
                  ]}
                >
                  <ContactField
                    contactValue={contactValue}
                    onContactChange={handleContactChange}
                  />
                </Form.Item>
              </Col>
              <Col span={2}></Col>
              <Col xs={12} md={10}>
                <Form.Item
                  name="homeProvince"
                  label="Home province"
                  rules={[
                    {
                      required: true,
                      message: "Please select your home province",
                    },
                  ]}
                >
                  <Select onChange={onHomeProvinceChange}>
                    {provinces.map((province) => (
                      <Option key={province.key}>{province.value}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={10}>
                <Form.Item
                  ref={addressRef}
                  name="address"
                  label="Address"
                  help={addressMsg}
                  validateStatus={addressValidity}
                  rules={[
                    {
                      required: addressRequired,
                      message: addressMsg,
                    },
                  ]}
                >
                  {!showAddressInfo ? (

                    <Button name="address" onClick={() => setShowModal(true)} ref={addressRef}>
                      Add Address
                    </Button>
                  ) : (
                    <div
                      style={{ maxWidth: 600, display: "flex", marginBottom: 20 }}
                    >
                      <Card

                        style={{
                          width: "100%",
                          justifyContent: "center",
                          marginBottom: 10,
                          backgroundColor: "rgb(250 250 250)",
                          borderBlockStyle: "solid",
                          borderColor: "#CCC",
                        }}
                        actions={[
                          <Button
                            key="Edit"
                            value="Edit"
                            type="default"
                            onClick={() => setShowModal(true)}
                          >
                            Edit
                          </Button>,
                        ]}
                      >
                        <Meta
                          avatar={<Avatar icon={<GlobalOutlined />} />}
                          title="Local Address"
                          description={`${address.street} ${address.cityOrTown} ${address.province} ${address.postalCode}`}
                        />
                      </Card>
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Card
              title="Preferred Placement and Employment Details"
              bordered={true}
              headStyle={{ border: 0, textAlign: "center" }}
            >
              <Row>
                <Col xs={12} md={10}>
                  <Form.Item
                    name="preferredProvince"
                    label="Preferred placement province"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please select your preferred placement province",
                      },
                    ]}
                  >
                    <Select onChange={onProvinceChange}>
                      {provinces.map((province) => (
                        <Option key={province.key}>{province.value}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={2}></Col>
                <Col xs={12} md={10}>
                  <Form.Item
                    name="preNYSActivity"
                    label="Current employment status"
                    rules={[
                      {
                        required: true,
                        message: "Please select your current employment status",
                      },
                    ]}
                  >
                    <Select onChange={onCurrentPlacementStatusChange}>
                      {activities.map((activity) => (
                        <Option key={activity.key}> {activity.value}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
            <Card
              title="Alternative Contact Person"
              bordered={true}
              headStyle={{ border: 0, textAlign: "center" }}
            >
              <Row>
                <Col xs={24} md={10}>
                  <Form.Item
                    name="alternativePersonFullName"
                    label="Full name"
                    rules={[
                      {
                        pattern: new RegExp("[A-Za-z]"),
                        message: "Provided charaters not allowed"
                      },
                      {
                        required: false,
                      }
                    ]}

                  >
                    <Input
                      name="alternativePersonFullName"
                      onBlur={onNameBlur}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}></Col>
                <Col xs={24} md={10}>
                  <Form.Item
                    name="alternativeCellNumber"
                    label="Cellphone No"
                    rules={[
                      {
                        required: false,
                      },
                    ]}
                  >
                    <ContactField
                      contactValue={altContactValue}
                      onContactChange={handleAltContactChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
            <br />
            <Row justify="end" style={{paddingBottom:"15px",paddingRight:"15px"}}>
              <Col xs={24}>
                <Button
                  key="2"
                  htmlType="submit"
                  form={formName}
                  type="primary"
                  shape="round"
                  className="right-align-button"
                >
                  Save Personal Information
                </Button>
              </Col>
            </Row>
          </Form>
          <AddressControl
            key="1"
            onCancel={handleCancel}
            visible={showModal}
            title="Edit Address"
            isLocal={true}
            onModalSubmit={handleModalSubmit}
            address={address}
          />
        </>
      )}
    </>
  );
};

export default PersonalInformation;
